import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../app/providers';
import { href, Link } from '../../app/router';
import { useSession } from '../auth/session';
import { Button, IconButton } from '../../ui';
import { notificationsKey, useNotifications } from './useNotifications';

/** Mentions and replies of the signed-in user (not for anonymous guests: they have no dashboard). */
export function NotificationsBell() {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const queryClient = useQueryClient();
  const signedIn = session.status === 'signed-in' && !session.user.isAnonymous;
  const notifications = useNotifications(signedIn);
  const [open, setOpen] = useState(false);
  if (!signedIn || !api.capabilities.comments) return null;
  const list = notifications.data ?? [];
  const unread = list.filter((n) => !n.readAt).length;
  const markAll = async () => {
    await api.markNotificationsRead().catch(() => undefined);
    await queryClient.invalidateQueries({ queryKey: notificationsKey });
  };
  return (
    <div className="relative">
      <IconButton
        label={unread ? t('comments.notifications.unread', { count: unread }) : t('comments.notifications.label')}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative h-8 w-8"
        icon={
          <>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 16V11a6 6 0 0112 0v5l2 2H4zM10 20h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
            {unread > 0 && (
              <span aria-hidden="true" className="absolute -right-1 -top-1 min-w-4 rounded-full bg-accent px-1 font-sans text-[10px] font-bold leading-4 text-accent-fg">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </>
        }
      />
      {open && (
        <div
          role="dialog"
          aria-label={t('comments.notifications.label')}
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          className="absolute left-0 top-10 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 rounded-xl bg-surface p-3 shadow-lg"
        >
          {list.length === 0 ? (
            <p className="font-sans text-sm text-fg-muted">{t('comments.notifications.empty')}</p>
          ) : (
            <ul className="flex max-h-96 flex-col gap-1 overflow-auto">
              {list.map((n) => (
                <li key={n.id}>
                  <Link
                    to={href('board', { boardId: n.boardId }, { thread: n.threadId })}
                    onClick={() => setOpen(false)}
                    className={`block rounded-md p-2 font-sans text-sm hover:bg-bg ${n.readAt ? 'text-fg-muted' : 'text-fg'}`}
                  >
                    <span className="block">{t(`comments.notifications.${n.kind}`, { author: n.authorName || t('comments.formerGuest'), board: n.boardTitle })}</span>
                    <span className="block truncate text-xs text-fg-muted">{n.excerpt}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {unread > 0 && (
            <Button onClick={() => void markAll()}>{t('comments.notifications.markAll')}</Button>
          )}
        </div>
      )}
    </div>
  );
}
